import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Checkbox,
  FormControlLabel,
  Collapse,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Stack,
  Divider,
  Autocomplete,
  alpha,
  useTheme,
  Chip,
  ButtonBase,
  Avatar,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import { generateConversationTaskChecklist, generateTeamSpaceTaskChecklist } from "../../api/client";
import { useUsers } from "../../hooks/useUsers";
import {
  useTeamBoard,
  useTeamBoardForSpace,
  useTeamBoardsList,
  useTeamBoardMutations,
  teamBoardKey,
} from "../../hooks/useTeamBoards";
import { useTeamBoardAction } from "../../pages/TeamTasks/hooks/useTeamBoardAction";

const IRRELEVANT_TYPES = new Set(["log", "survey", "automaticanswer", "system", "call", "video_invite"]);

function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function messageText(msg) {
  return msg?.text || msg?.textPlain || msg?.content || "";
}

function messagePreview(msg) {
  const plain = stripHtml(messageText(msg));
  if (!plain) return "";
  return plain.length > 220 ? `${plain.slice(0, 220)}…` : plain;
}

function messageKind(msg) {
  return msg?.type || msg?.messageType || "text";
}

function messageLabel(msg, t) {
  if (messageKind(msg) === "note") return t("aiTasksMessageNote");
  const senderType = msg?.senderType || msg?.authorType;
  const senderName = msg?.senderName || msg?.authorName;
  if (senderType === "client") return senderName || t("aiTasksMessageClient");
  if (senderType === "agent" || senderType === "chatbot") {
    return senderName || t("aiTasksMessageAgent");
  }
  if (senderType === "user") {
    return senderName || t("aiTasksMessageAgent");
  }
  return senderName || t("aiTasksMessageOther");
}

function messageTimeLabel(msg) {
  const raw =
    msg?.createdAt ||
    msg?.lastUpdate ||
    msg?.timestamp ||
    msg?.created_at ||
    msg?.updatedAt ||
    null;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    const asText = String(raw).trim();
    // Already formatted like "2026-09-16 17:16"
    const match = asText.match(/(\d{2}:\d{2})/);
    return match ? match[1] : "";
  }
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function senderInitials(label) {
  const parts = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function filterMessagesForAiTasks(messages = []) {
  return (messages || []).filter((msg) => {
    if (!msg?.id) return false;
    if (msg?.deletedAt || msg?.deleted_at) return false;
    if (IRRELEVANT_TYPES.has(messageKind(msg))) return false;
    return Boolean(messagePreview(msg));
  });
}

function findNewestCardInColumn(board, columnId) {
  const col = (board?.columns || []).find((c) => c.id === columnId);
  if (!col?.cards?.length) return null;
  return col.cards.reduce((best, card) => (card.id > best.id ? card : best));
}

function findNewestChecklistOnCard(board, cardId) {
  for (const col of board?.columns || []) {
    const card = (col.cards || []).find((c) => c.id === cardId);
    if (!card?.checklists?.length) continue;
    return card.checklists.reduce((best, cl) => (cl.id > best.id ? cl : best));
  }
  return null;
}

function userLabel(user) {
  return user?.nome || user?.name || String(user?.id ?? "");
}

function assigneesForCreate({ isMultipleTasks, assigneeMode, globalAssignee, itemAssignee }) {
  if (isMultipleTasks) {
    if (assigneeMode === "individual" && itemAssignee) {
      return [Number(itemAssignee.id)];
    }
    if (assigneeMode === "shared" && globalAssignee) {
      return [Number(globalAssignee.id)];
    }
    return [];
  }
  if (globalAssignee) return [Number(globalAssignee.id)];
  return [];
}

const AiGenerateTasksDialog = ({
  open,
  onClose,
  conversationId,
  spaceId = null,
  spaceType = null,
  leadId = null,
  teamCompany = null,
  messages = [],
  defaultColumnId,
  mutations: mutationsProp,
  run: runProp,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const { data: usersData } = useUsers();
  const users = usersData?.users || usersData || [];
  const { run: runFromContext } = useTeamBoardAction();

  const [step, setStep] = useState("intro");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [outputMode, setOutputMode] = useState("single_task");
  const [assigneeMode, setAssigneeMode] = useState("shared");
  const [globalAssignee, setGlobalAssignee] = useState(null);
  const [sourceMode, setSourceMode] = useState("all");
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [error, setError] = useState(null);
  const [taskTitle, setTaskTitle] = useState("");
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);

  const isTeamSpaceSource = Boolean(spaceId);
  const canUseSpaceBoard = isTeamSpaceSource && spaceType === "channel";
  const contextLeadId = leadId || conversationId || null;
  const eligibleMessages = useMemo(() => filterMessagesForAiTasks(messages), [messages]);
  const selectableMessagesNewestFirst = useMemo(
    () => [...eligibleMessages].reverse(),
    [eligibleMessages]
  );
  const dialogSessionRef = useRef(null);
  const boardInitRef = useRef(null);

  const { data: workspaceBoardsPayload, isLoading: boardsLoading } = useTeamBoardsList({
    workspace: 1,
    enabled: open && isTeamSpaceSource,
  });
  const workspaceBoards = workspaceBoardsPayload?.boards || [];
  const { data: spaceBoard } = useTeamBoardForSpace(spaceId, {
    enabled: open && canUseSpaceBoard && Boolean(spaceId),
  });

  const boardOptions = useMemo(() => {
    if (!isTeamSpaceSource) return [];
    const list = Array.isArray(workspaceBoards) ? [...workspaceBoards] : [];
    if (
      canUseSpaceBoard &&
      spaceBoard?.id &&
      !list.some((b) => Number(b.id) === Number(spaceBoard.id))
    ) {
      list.unshift(spaceBoard);
    }
    return list;
  }, [isTeamSpaceSource, workspaceBoards, spaceBoard, canUseSpaceBoard]);

  const targetBoardId = isTeamSpaceSource ? selectedBoard?.id ?? null : null;
  const { data: targetBoardDetail, isLoading: targetBoardLoading } = useTeamBoard(targetBoardId, {
    enabled: open && isTeamSpaceSource && Boolean(targetBoardId),
  });

  const targetColumns = useMemo(() => {
    if (!isTeamSpaceSource) return [];
    return targetBoardDetail?.columns || selectedBoard?.columns || [];
  }, [isTeamSpaceSource, targetBoardDetail, selectedBoard]);

  const resolvedDefaultColumnId = useMemo(() => {
    if (!isTeamSpaceSource) return defaultColumnId;
    const col = targetColumns.find((c) => !c.isDone) || targetColumns[0] || null;
    return col?.id ?? null;
  }, [isTeamSpaceSource, defaultColumnId, targetColumns]);

  const boardCacheKeys = useMemo(() => {
    const keys = [];
    if (targetBoardId) keys.push(teamBoardKey(targetBoardId));
    if (spaceId && spaceBoard?.id && Number(spaceBoard.id) === Number(targetBoardId)) {
      keys.push(["teamBoardForSpace", Number(spaceId)]);
    }
    return keys;
  }, [targetBoardId, spaceId, spaceBoard?.id]);

  const boardMutations = useTeamBoardMutations(targetBoardId, {
    extraCacheKeys: boardCacheKeys,
    contextLeadId: contextLeadId || null,
  });

  const mutations = isTeamSpaceSource ? boardMutations : mutationsProp;
  const run = isTeamSpaceSource ? runFromContext : runProp;

  useEffect(() => {
    if (!open) {
      dialogSessionRef.current = null;
      boardInitRef.current = null;
      setCreating(false);
      return;
    }
    const sessionKey = isTeamSpaceSource
      ? `space:${String(spaceId)}`
      : `conversation:${String(conversationId ?? "")}`;
    if (dialogSessionRef.current === sessionKey) return;
    dialogSessionRef.current = sessionKey;

    setStep("intro");
    setAdvancedOpen(false);
    setOutputMode("single_task");
    setAssigneeMode("shared");
    setGlobalAssignee(null);
    setSourceMode("all");
    setSelectedMessageIds(filterMessagesForAiTasks(messages).map((m) => m.id));
    setError(null);
    setTaskTitle("");
    setItems([]);
    setCreating(false);
    setSelectedBoard(null);
  }, [open, conversationId, spaceId, isTeamSpaceSource, messages]);

  useEffect(() => {
    if (!open || !isTeamSpaceSource) return;
    if (selectedBoard?.id) return;
    if (canUseSpaceBoard && spaceBoard?.id) {
      boardInitRef.current = String(spaceId);
      setSelectedBoard(spaceBoard);
      return;
    }
    if (boardOptions.length) {
      boardInitRef.current = String(spaceId);
      setSelectedBoard(boardOptions[0]);
    }
  }, [open, isTeamSpaceSource, spaceId, spaceBoard, boardOptions, selectedBoard, canUseSpaceBoard]);

  const boardOptionLabel = (board) => {
    if (!board) return "";
    if (canUseSpaceBoard && spaceBoard?.id && Number(board.id) === Number(spaceBoard.id)) {
      return t("aiTasksChannelBoardOption", { name: board.name || `#${board.id}` });
    }
    return board.name || t("aiTasksBoardFallback", { id: board.id });
  };

  const toggleMessageId = (id) => {
    setSelectedMessageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllEligibleMessages = () => {
    setSelectedMessageIds(eligibleMessages.map((m) => m.id));
  };

  const clearEligibleMessages = () => {
    setSelectedMessageIds([]);
  };

  const handleGenerate = async () => {
    if (!conversationId && !spaceId) return;
    if (isTeamSpaceSource && !selectedBoard?.id) {
      setError(t("aiTasksBoardRequired"));
      return;
    }
    setError(null);
    setStep("loading");

    const body = {
      ...(sourceMode === "selected" && selectedMessageIds.length
        ? { messageIds: selectedMessageIds }
        : {}),
      outputMode,
    };

    try {
      const data = isTeamSpaceSource
        ? await generateTeamSpaceTaskChecklist(spaceId, body, teamCompany)
        : await generateConversationTaskChecklist(conversationId, body);
      const generatedItems = (data?.checklistItems || []).map((item, index) => ({
        id: `item-${index}`,
        title: item.title || "",
        description: item.description || "",
        selected: true,
        assignee: null,
      }));

      if (!generatedItems.length) {
        setError(t("aiTasksEmptyResult"));
        setStep("intro");
        return;
      }

      setTaskTitle(
        data?.taskTitle ||
          (isTeamSpaceSource ? t("aiTasksDefaultTitleTeam") : t("aiTasksDefaultTitle"))
      );
      setItems(generatedItems);
      setAssigneeMode("shared");
      setGlobalAssignee(null);
      setStep("review");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("aiTasksGenerateError");
      setError(msg);
      setStep("intro");
    }
  };

  const handleCreate = async () => {
    const selected = items.filter((item) => item.selected && item.title.trim());
    const columnId = resolvedDefaultColumnId;
    if (!selected.length || !columnId || !mutations || creating) return;

    let succeeded = false;
    const leadPayload = contextLeadId
      ? { leadId: contextLeadId, contextLeadId }
      : {};

    if (outputMode === "multiple_tasks") {
      setCreating(true);
      setError(null);
      try {
        let created = 0;
        for (let i = 0; i < selected.length; i++) {
          const item = selected[i];
          const isLast = i === selected.length - 1;
          const result = await run(
            () =>
              mutations.createCard.mutateAsync({
                columnId,
                title: item.title.trim(),
                description: item.description?.trim() || null,
                ...leadPayload,
                assignees: assigneesForCreate({
                  isMultipleTasks: true,
                  assigneeMode,
                  globalAssignee,
                  itemAssignee: item.assignee,
                }),
              }),
            {
              context: "createCard",
              successKey: isLast
                ? selected.length > 1
                  ? "aiTasksCreateMultipleSuccess"
                  : "successCardCreated"
                : undefined,
            }
          );
          if (result === null) break;
          created++;
        }
        succeeded = created > 0;
      } catch (err) {
        setError(err?.message || t("aiTasksCreateError"));
      } finally {
        setCreating(false);
        if (succeeded) onClose?.(true);
      }
      return;
    }

    const title = taskTitle.trim();
    if (!title) return;

    setCreating(true);
    setError(null);
    let cardCreated = false;

    try {
      const boardAfterCard = await run(
        () =>
          mutations.createCard.mutateAsync({
            columnId,
            title,
            ...leadPayload,
            assignees: assigneesForCreate({
              isMultipleTasks: false,
              globalAssignee,
            }),
          }),
        { context: "createCard", successKey: "successCardCreated" }
      );

      if (!boardAfterCard) return;

      const card = findNewestCardInColumn(boardAfterCard, columnId);
      if (!card?.id) {
        setError(t("aiTasksCreateError"));
        return;
      }
      cardCreated = true;

      const boardAfterChecklist = await run(
        () =>
          mutations.createChecklist.mutateAsync({
            cardId: card.id,
            title: t("checklistDefaultTitle"),
          }),
        { context: "checklist" }
      );

      if (!boardAfterChecklist) return;

      const checklist = findNewestChecklistOnCard(boardAfterChecklist, card.id);
      if (!checklist?.id) {
        setError(t("aiTasksCreateError"));
        succeeded = true;
        return;
      }

      for (let i = 0; i < selected.length; i++) {
        const item = selected[i];
        const payload = {
          cardId: card.id,
          checklistId: checklist.id,
          title: item.title.trim(),
        };
        const isLast = i === selected.length - 1;
        const result = isLast
          ? await run(
              () => mutations.createChecklistItem.mutateAsync(payload),
              { context: "checklist" }
            )
          : await mutations.createChecklistItem.mutateAsync(payload).catch((err) => {
              console.error("[aiTasks] checklist item", err);
              return null;
            });
        if (isLast && result === null) break;
      }

      succeeded = true;
    } catch (err) {
      setError(err?.message || t("aiTasksCreateError"));
      if (cardCreated) succeeded = true;
    } finally {
      setCreating(false);
      if (succeeded) onClose?.(true);
    }
  };

  const selectedItemCount = items.filter((item) => item.selected && item.title.trim()).length;
  const isMultipleTasks = outputMode === "multiple_tasks";
  const hasTargetBoard = !isTeamSpaceSource || Boolean(selectedBoard?.id && resolvedDefaultColumnId);
  const canGenerate =
    hasTargetBoard &&
    (sourceMode === "all" || (sourceMode === "selected" && selectedMessageIds.length > 0));
  const canConfirmCreate =
    hasTargetBoard &&
    (isMultipleTasks
      ? selectedItemCount > 0
      : Boolean(taskTitle.trim()) && selectedItemCount > 0);

  const targetBoardPicker = isTeamSpaceSource ? (
    <Box sx={{ mb: 2 }}>
      <Autocomplete
        options={boardOptions}
        loading={boardsLoading}
        value={selectedBoard}
        onChange={(_, value) => setSelectedBoard(value)}
        getOptionLabel={boardOptionLabel}
        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("aiTasksTargetBoardLabel")}
            placeholder={t("aiTasksTargetBoardPlaceholder")}
            helperText={
              targetBoardLoading
                ? t("aiTasksTargetBoardLoading")
                : selectedBoard && !resolvedDefaultColumnId
                  ? t("aiTasksTargetBoardNoColumns")
                  : spaceType === "dm"
                    ? t("aiTasksDmBoardHint")
                    : t("aiTasksTargetBoardHint")
            }
          />
        )}
      />
    </Box>
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : () => onClose(false)}
      maxWidth="sm"
      fullWidth
      aria-labelledby="ai-generate-tasks-title"
    >
      <DialogTitle id="ai-generate-tasks-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon color="primary" fontSize="small" />
        {t("aiTasksDialogTitle")}
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: step === "loading" ? 180 : undefined }}>
        {step === "intro" && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              {isTeamSpaceSource ? t("aiTasksDialogIntroTeam") : t("aiTasksDialogIntro")}
            </Typography>

            {targetBoardPicker}

            <Alert severity="info" icon={<AutoAwesomeIcon fontSize="inherit" />} sx={{ mb: 2 }}>
              {t("aiTasksDialogHint")}
            </Alert>

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}

            <Button
              size="small"
              onClick={() => setAdvancedOpen((v) => !v)}
              endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mb: advancedOpen ? 1 : 0, textTransform: "none", fontWeight: 600 }}
            >
              {t("aiTasksAdvancedOptions")}
            </Button>

            <Collapse in={advancedOpen}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  bgcolor: alpha(theme.palette.action.hover, 0.04),
                }}
              >
                <FormControl component="fieldset" sx={{ width: "100%", mb: 2 }}>
                  <FormLabel component="legend" sx={{ fontSize: "0.8125rem", fontWeight: 700, mb: 0.5 }}>
                    {t("aiTasksOutputLabel")}
                  </FormLabel>
                  <RadioGroup
                    value={outputMode}
                    onChange={(e) => setOutputMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="single_task"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {t("aiTasksOutputSingleTitle")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("aiTasksOutputSingleHint")}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 0.5 }}
                    />
                    <FormControlLabel
                      value="multiple_tasks"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {t("aiTasksOutputMultipleTitle")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("aiTasksOutputMultipleHint")}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start" }}
                    />
                  </RadioGroup>
                </FormControl>

                <Divider sx={{ mb: 2 }} />

                <FormControl component="fieldset" sx={{ width: "100%" }}>
                  <FormLabel component="legend" sx={{ fontSize: "0.8125rem", fontWeight: 700, mb: 0.5 }}>
                    {t("aiTasksSourceLabel")}
                  </FormLabel>
                  <RadioGroup
                    value={sourceMode}
                    onChange={(e) => setSourceMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {isTeamSpaceSource ? t("aiTasksSourceAllTeam") : t("aiTasksSourceAll")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("aiTasksSourceAllHint")}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 0.5 }}
                    />
                    <FormControlLabel
                      value="selected"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {t("aiTasksSourceSelected")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("aiTasksSourceSelectedHint")}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start" }}
                    />
                  </RadioGroup>
                </FormControl>

                {sourceMode === "selected" ? (
                  <Box
                    sx={{
                      mt: 1.25,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                      bgcolor: theme.palette.background.paper,
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        px: 1.25,
                        py: 1,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                        bgcolor: alpha(theme.palette.action.hover, 0.04),
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {t("aiTasksPickMessagesTitle")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t("aiTasksPickMessagesHint")}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={selectedMessageIds.length ? "primary" : "default"}
                        variant={selectedMessageIds.length ? "filled" : "outlined"}
                        label={t("aiTasksPickCount", {
                          selected: selectedMessageIds.length,
                          total: eligibleMessages.length,
                        })}
                        sx={{ fontWeight: 700, height: 24 }}
                      />
                    </Stack>

                    {eligibleMessages.length > 0 ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ px: 1.25, pt: 1, pb: 0.5 }}
                      >
                        <Button
                          size="small"
                          onClick={selectAllEligibleMessages}
                          disabled={selectedMessageIds.length === eligibleMessages.length}
                          sx={{ textTransform: "none", fontWeight: 600, minWidth: 0, px: 1 }}
                        >
                          {t("aiTasksPickSelectAll")}
                        </Button>
                        <Button
                          size="small"
                          onClick={clearEligibleMessages}
                          disabled={selectedMessageIds.length === 0}
                          sx={{ textTransform: "none", fontWeight: 600, minWidth: 0, px: 1 }}
                        >
                          {t("aiTasksPickClear")}
                        </Button>
                      </Stack>
                    ) : null}

                    <Box sx={{ maxHeight: 280, overflow: "auto", px: 1, pb: 1, pt: 0.5 }}>
                      {eligibleMessages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 1.5 }}>
                          {t("aiTasksNoEligibleMessages")}
                        </Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {selectableMessagesNewestFirst.map((msg) => {
                            const preview = messagePreview(msg);
                            const checked = selectedMessageIds.includes(msg.id);
                            const label = messageLabel(msg, t);
                            const time = messageTimeLabel(msg);
                            return (
                              <ButtonBase
                                key={msg.id}
                                onClick={() => toggleMessageId(msg.id)}
                                focusRipple
                                sx={{
                                  display: "block",
                                  width: "100%",
                                  textAlign: "left",
                                  borderRadius: 1.75,
                                  border: `1px solid ${
                                    checked
                                      ? alpha(theme.palette.primary.main, 0.45)
                                      : alpha(theme.palette.divider, 0.7)
                                  }`,
                                  bgcolor: checked
                                    ? alpha(theme.palette.primary.main, 0.06)
                                    : alpha(theme.palette.background.default, 0.35),
                                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                                  "&:hover": {
                                    borderColor: checked
                                      ? theme.palette.primary.main
                                      : alpha(theme.palette.text.primary, 0.28),
                                    bgcolor: checked
                                      ? alpha(theme.palette.primary.main, 0.09)
                                      : alpha(theme.palette.action.hover, 0.06),
                                  },
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="flex-start"
                                  spacing={1}
                                  sx={{ p: 1.1 }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={checked}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={{ p: 0.25, mt: 0.15, pointerEvents: "none" }}
                                  />
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      mt: 0.15,
                                      fontSize: "0.7rem",
                                      fontWeight: 700,
                                      bgcolor: checked
                                        ? alpha(theme.palette.primary.main, 0.18)
                                        : alpha(theme.palette.text.primary, 0.08),
                                      color: checked ? "primary.main" : "text.secondary",
                                    }}
                                  >
                                    {senderInitials(label)}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack
                                      direction="row"
                                      alignItems="baseline"
                                      spacing={0.75}
                                      sx={{ mb: 0.2 }}
                                    >
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.primary"
                                        noWrap
                                        sx={{ flex: 1, minWidth: 0 }}
                                      >
                                        {label}
                                      </Typography>
                                      {time ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
                                        >
                                          {time}
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                      sx={{ lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                    >
                                      {preview}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </ButtonBase>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Collapse>
          </Box>
        )}

        {step === "loading" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t("aiTasksGenerating")}
            </Typography>
          </Box>
        )}

        {step === "review" && (
          <Box>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              {isMultipleTasks ? t("aiTasksReviewIntroMultiple") : t("aiTasksReviewIntro")}
            </Typography>

            {targetBoardPicker}

            {!isMultipleTasks ? (
              <TextField
                label={t("aiTasksTaskTitleLabel")}
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
            ) : null}

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                bgcolor: alpha(theme.palette.action.hover, 0.03),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  {t("aiTasksAssigneeSectionTitle")}
                </Typography>
              </Stack>

              {isMultipleTasks ? (
                <FormControl component="fieldset" sx={{ width: "100%", mb: assigneeMode === "shared" ? 1.5 : 1 }}>
                  <RadioGroup
                    value={assigneeMode}
                    onChange={(e) => setAssigneeMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="shared"
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">{t("aiTasksAssigneeShared")}</Typography>
                      }
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">{t("aiTasksAssigneeIndividual")}</Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, lineHeight: 1.5 }}>
                  {t("aiTasksAssigneeSingleHint")}
                </Typography>
              )}

              {(!isMultipleTasks || assigneeMode === "shared") && (
                <Autocomplete
                  options={users}
                  getOptionLabel={userLabel}
                  value={globalAssignee}
                  onChange={(_, value) => setGlobalAssignee(value)}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("aiTasksAssigneeLabel")}
                      placeholder={t("cardAssigneesPlaceholder")}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                />
              )}
            </Box>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {isMultipleTasks ? t("aiTasksTasksListLabel") : t("aiTasksChecklistLabel")}
            </Typography>

            <Stack spacing={1}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    p: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={item.selected}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((row) =>
                          row.id === item.id ? { ...row, selected: e.target.checked } : row
                        )
                      )
                    }
                    sx={{ mt: 0.25, p: 0.5, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      value={item.title}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((row) =>
                            row.id === item.id ? { ...row, title: e.target.value } : row
                          )
                        )
                      }
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={3}
                      placeholder={
                        isMultipleTasks ? t("aiTasksTaskPlaceholder") : t("aiTasksItemPlaceholder")
                      }
                    />
                    {isMultipleTasks && assigneeMode === "individual" ? (
                      <Autocomplete
                        options={users}
                        getOptionLabel={userLabel}
                        value={item.assignee}
                        onChange={(_, value) =>
                          setItems((prev) =>
                            prev.map((row) =>
                              row.id === item.id ? { ...row, assignee: value } : row
                            )
                          )
                        }
                        size="small"
                        sx={{ mt: 1 }}
                        disabled={!item.selected}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("aiTasksAssigneeLabel")}
                            placeholder={t("cardAssigneesPlaceholder")}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                      />
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Stack>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              {isMultipleTasks
                ? t("aiTasksSelectedTasksCount", { count: selectedItemCount })
                : t("aiTasksSelectedCount", { count: selectedItemCount })}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {step === "review" ? (
          <>
            <Button onClick={() => setStep("intro")} disabled={creating}>
              {t("aiTasksBack")}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => onClose(false)} disabled={creating}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || !canConfirmCreate}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {creating
                ? t("aiTasksCreating")
                : isMultipleTasks
                  ? t("aiTasksCreateMultiple", { count: selectedItemCount })
                  : t("aiTasksCreate", { count: selectedItemCount })}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => onClose(false)} disabled={step === "loading"}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={step === "loading" || !canGenerate}
              startIcon={
                step === "loading" ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
              }
            >
              {step === "loading" ? t("aiTasksGeneratingShort") : t("aiTasksGenerate")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiGenerateTasksDialog;
